'use client';

import useCartProduct from '@/_hooks/useCartProduct';
import productService from '@/_service/product.service';
import {
  ADD_TO_CART,
  DECREMENT,
  INCREMENT,
  REMOVE,
  REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION,
} from '@/_store/cart/cart.slice';
import { IRedux } from '@/_store/types';
import {
  CurrentVariantType,
  ISavedModifier,
} from '@/_types';
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ComboProducts from './components/ComboProducts';
import NumberToPrice from '@/_components/NumberToPrice';
import { Link, useRouter } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import Counter from '../Counter';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { LinkButton } from '@/components/ui/link-button';
import useGTM from '@/_hooks/useGTM';
import { track } from '@/_utils/utm';
import { useTelegram } from '@/_providers/telegram';
import useProductDiscount from '@/_hooks/useProductDiscount';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import ProductHeader from '../ProductHeader';
import { IProduct } from '@/entities/product/model/types';
import { BRAND_NAMES } from '@/_utils/Constants';
import ConfirmModal from '@/_components/ConfirmModal';
import { onOpenMap } from '@/_store/common/common.slice';
import { needsDestinationSelection } from '@/_lib/destination-gate';
import { ICategory } from '@/_types';

const Combo = ({
  data,
  onSuccess,
  onRedirectToCart,
  isModal,
}: {
  data: IProduct;
  onSuccess?: () => void;
  onRedirectToCart?: () => void;
  isModal?: boolean;
}) => {
  const [quantity, setQuantity] = useState(1);
  const [currentVariant, setCurrentVariant] = useState<CurrentVariantType>({});

  const { branches } = useSelector((state: IRedux) => state.common);
  const { data: settingsData } = useSelector((state: IRedux) => state.settings);
  const { permissions, is_hall, order_type } = useSelector(
    (state: IRedux) => state.session,
  );

  const router = useRouter();
  const t = useTranslations();
  const dispatch = useDispatch();
  const currentLocale = useCustomLocale();

  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]
  const currentBrandName = BRAND_NAMES?.[shipperId]

  const cartProducts = useSelector(
    (state: IRedux) => state.cart.cart?.[shipperId]?.products ?? []
  );
  const [showRestrictedConfirm, setShowRestrictedConfirm] = useState(false);
  const category = data?.categories?.[0] as ICategory | undefined;
  const newProductRestricted = category?.is_shopping_cart_restricted ?? false;
  const hasOppositeCategoryInCart = useMemo(
    () =>
      cartProducts.some(
        (p) => (p.category_is_restricted ?? false) !== newProductRestricted
      ),
    [cartProducts, newProductRestricted]
  );


  const { sendGTM } = useGTM();
  const { tg_chat_id } = useTelegram();

  const { productInCart } = useCartProduct(data?.id, [], currentVariant);
  const { discounts } = useProductDiscount();
  const price: number = useMemo(() => data?.out_price || 0, [data?.out_price]);

  const selectedMenuId = branch?.menu_id || '';

  const totalDiscountPrice = useMemo(() => {
    if (!discounts?.length) return 0;

    return discounts.reduce((acc, discount) => {
      if (discount.product_and_discount) {
        acc += Number(discount.product_and_discount[data?.id] ?? 0);
      }

      if (discount.discount_mode === 'fixed') {
        acc -= Number(discount.discount_amount || 0);
      }

      if (discount.discount_mode === 'percent') {
        acc -= Number(
          ((data?.out_price || 0) * (discount.discount_amount || 0)) / 100,
        );
      }

      return acc;
    }, 0);
  }, [discounts, data?.id, data?.out_price]);

  const { data: comboData } = useQuery({
    queryKey: ['combo', data?.id],
    queryFn: () =>
      productService
        .getComboById(data?.id, {
          menu_id: selectedMenuId,
        })
        .then((res) => res?.data),
    enabled: Boolean(data?.type === 'combo' && data?.id),
  });

  const additionalPrice = useMemo(
    () =>
      Object.values(currentVariant).reduce((sum, variant) => {
        const modifiersTotal = variant?.order_modifiers?.reduce(
          (acc, modifier) => {
            return acc + modifier.modifier_price * modifier.modifier_quantity;
          },
          0,
        );

        return sum + modifiersTotal;
      }, 0),
    [currentVariant],
  );


  const addToCartCombo = useCallback(() => {
    if (!data?.id || data?.type !== 'combo') return;
    dispatch(
      ADD_TO_CART({
        image: data?.image || '',
        category_id: category?.id,
        category_name: category?.title?.[currentLocale],
        key: '',
        comment: '',
        in_stop: false,
        default_product: false,
        discount_percent:
          totalDiscountPrice !== 0
            ? Math.floor((totalDiscountPrice / price) * 100)
            : 0,
        product_name: data.title?.[currentLocale],
        price_with_discount: price + totalDiscountPrice + additionalPrice,
        price: price + additionalPrice,
        product_id: data?.id,
        type: data?.type,
        variants: currentVariant,
        order_modifiers: [],
        quantity: quantity,
        category_is_restricted: category?.is_shopping_cart_restricted,
        brand: currentBrandName,
        shipper_id: shipperId,
        source_id: '',
        branch_id: branch?.id || '',
        menu_id: branch?.menu_id || '',
      }),
    );
    track({
      action_type: 'add_to_cart',
      tg_chat_id,
    });
    sendGTM('add_to_cart', { product: data, quantity });
    if (onSuccess) {
      onSuccess();
    } else {
      router.push('/');
    }
  }, [data, dispatch, totalDiscountPrice, price, additionalPrice, currentLocale, currentVariant, quantity, category, currentBrandName, shipperId, branch, tg_chat_id, sendGTM, onSuccess, router]);

  const onConfirmReplaceCart = useCallback(() => {
    dispatch(
      REMOVE_PRODUCTS_BY_OPPOSITE_RESTRICTION({
        shipper_id: shipperId,
        is_restricted: newProductRestricted,
      })
    );
    setShowRestrictedConfirm(false);
    addToCartCombo();
  }, [dispatch, shipperId, newProductRestricted, addToCartCombo]);

  const onOrderClick = () => {
    if (
      needsDestinationSelection({
        isHall: is_hall,
        orderType: order_type?.type,
        branchId: branch?.id,
        geolocation: permissions.geolocation,
      })
    ) {
      dispatch(onOpenMap());
      return;
    }

    if (data?.id && data?.type === 'combo') {
      if (hasOppositeCategoryInCart) {
        setShowRestrictedConfirm(true);
      } else {
        addToCartCombo();
      }
    }
  };

  const onRemoveProduct = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
        name: settingsData?.data?.shipper_name,
      });
      dispatch(REMOVE({ key: productInCart?.key, shipper_id: shipperId }));
      setQuantity(1);
    }
  };

  useEffect(() => {
    if (productInCart?.quantity) {
      setQuantity(productInCart?.quantity);
    }
  }, []);


  const onAddVariant = (
    group_id: string,
    variant: IProduct,
    mods: ISavedModifier[],
    quantity: number,
  ) => {
    setCurrentVariant((prevState) => ({
      ...prevState,
      [group_id]: {
        ...prevState?.[group_id],
        group_id,
        quantity,
        variant_id: variant?.id,
        variant_name: variant?.title?.[currentLocale],
        order_modifiers: mods,
        price: variant?.out_price,
        image: variant?.image,
      },
    }));
  };

  const checkedAllCombos = Object.values(currentVariant)?.some(
    (el) => el?.variant_id === '',
  );

  return (
    <>
      <Stack
        pr={2}
        mr={4}
        h={'full'}
        mt={6}
        pb={isModal ? 0 : 20}
        maxHeight={isModal ? 'auto' : '620px'}
        overflow={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            width: '3px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#1A202433',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#FFFFFF4D',
          },
        }}
      >
        <ProductHeader data={data} isModal={isModal} />
        <Box flex={1} px={4} mt={6}>
          <Text
            fontSize={{ base: 'xs', lg: 'md' }}
            textTransform={'uppercase'}
            color={'text.muted'}
            fontWeight={500}
            ml={{ base: 4, lg: 0 }}
          >
            {t('assembly_of_the_set')}
          </Text>
          <ComboProducts
            comboData={comboData}
            currentVariant={currentVariant}
            onAddVariant={onAddVariant}
          />
        </Box>
      </Stack>
      <HStack
        position={isModal ? 'sticky' : 'absolute'}
        bottom={0}
        left={0}
        bg='background.card'
        zIndex={10}
        w='full'
        py={{ base: 4, lg: 3 }}
        px={{ base: 4, lg: 4 }}
        gap={{ base: 4, lg: 6 }}
        borderTop='1px solid #00000014'
        boxShadow={{ base: '0 -4px 6px -2px #00000014', lg: 'none' }}
      >
        <Flex
          w='full'
          justifyContent='space-between'
          fontSize={{ base: 'sm', lg: '2xl' }}
          fontWeight={600}
          color='text'
        >
          <Text as='span'>
            <NumberToPrice
              unstyled
              value={
                productInCart?.quantity
                  ? (price + additionalPrice + totalDiscountPrice) *
                  productInCart.quantity
                  : (price + additionalPrice + totalDiscountPrice) * quantity
              }
            />
          </Text>
        </Flex>
        <Flex gap={4} w='full'>
          <Counter
            height={{ base: '52px', lg: '56px' }}
            variable={
              productInCart?.quantity ? productInCart.quantity : quantity
            }
            onIncrease={() =>
              productInCart?.quantity
                ? dispatch(INCREMENT({ key: productInCart?.key, shipper_id: shipperId }))
                : setQuantity((prevState) => ++prevState)
            }
            onDecrease={() =>
              productInCart?.quantity
                ? productInCart.quantity > 1
                  ? dispatch(DECREMENT({ key: productInCart?.key, shipper_id: shipperId }))
                  : onRemoveProduct()
                : quantity > 1
                  ? setQuantity((prevState) => --prevState)
                  : setQuantity(1)
            }
            color='text'
          />
          {Boolean(productInCart) ? (
            <>
              {onRedirectToCart ? (
                <Button
                  flex={1}
                  bg='primary'
                  color='primary.text'
                  _hover={{
                    bg: 'primary.hover',
                  }}
                  rounded={16}
                  fontWeight={600}
                  h={{ base: '52px', lg: '56px' }}
                  fontSize={{ base: 'sm', lg: 'xl' }}
                  onClick={onRedirectToCart}
                >
                  {t('go_to_cart')}
                </Button>
              ) : (
                <LinkButton
                  as={Link}
                  flex={1}
                  h={{ base: '52px', lg: '56px' }}
                  href={'/cart'}
                  bg='primary'
                  color='primary.text'
                  _hover={{
                    bg: 'primary.hover',
                  }}
                  rounded={16}
                  fontWeight={600}
                  fontSize={{ base: 'sm', lg: 'xl' }}
                >
                  {t('go_to_cart')}
                </LinkButton>
              )}
            </>
          ) : (
            <Button
              onClick={() => onOrderClick()}
              disabled={Boolean(
                comboData && comboData?.groups?.length > 0
                  ? checkedAllCombos ||
                  Object.keys(currentVariant)?.length !==
                  comboData?.groups?.length
                  : !data,
              )}
              h={{ base: '52px', lg: '56px' }}
              flex={1}
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              rounded={16}
              fontWeight={600}
              loading={data?.has_modifier}
              fontSize={{ base: 'sm', lg: 'xl' }}
            >
              {t('add_to_cart')}
            </Button>
          )}
        </Flex>
      </HStack>
      <ConfirmModal
        isOpen={showRestrictedConfirm}
        onClose={() => setShowRestrictedConfirm(false)}
        onConfirm={onConfirmReplaceCart}
        title={t('attention')}
        description={t('cart_restricted_confirm_message')}
        text={{ confirm: 'yes', cancel: 'no' }}
      />
    </>
  );
};

export default Combo;
